/**
 * FirstDecisionGate.jsx
 *
 * Forces the "first decision of the day" before any trade is placed.
 * Shows a data-driven pre-flight briefing — each item auto-answers itself
 * from available market data so the trader learns to read the indicators,
 * not just tick boxes blindly.
 *
 * Three phases:
 *   PRE-MARKET  — prior-day data only; commit to a plan before the bell
 *   OPENING     — hold plan, intraday still forming (< 30 min in)
 *   LIVE        — real regime readable; gate flags any plan vs reality conflict
 *
 * Plan persists to KV keyed by ET date — survives reloads and device switches.
 * Pre-market data bootstraps on mount independent of the alert engine.
 */

import { useState, useEffect, useRef } from 'react';
import { dayPlanGet, dayPlanPut, BASE, getToken } from '../../lib/api';
import { analyzePreMarket } from '../../lib/sessionRegime';

// ── Helpers ───────────────────────────────────────────────────────────────────

function etDateStr() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

function parseBars(data) {
  if (data?.bars?.length) {
    return data.bars.filter(b => b.c != null)
      .map(b => ({ time: b.t / 1000, open: b.o, high: b.h, low: b.l, close: b.c, volume: b.v ?? 0 }));
  }
  const result = data?.chart?.result?.[0];
  if (!result) return [];
  const { timestamp: ts, indicators: { quote: [q] } } = result;
  return ts.map((t, i) => ({ time: t, open: q.open[i], high: q.high[i], low: q.low[i], close: q.close[i], volume: q.volume[i] ?? 0 }))
    .filter(b => b.close != null);
}

// VIX reading → plain label + colour + explanation + nudge
function classifyVix(price) {
  if (price == null) return null;
  if (price < 13) return { label: 'Very calm',    color: 'var(--up)',   note: 'Market is very relaxed. Options premiums are cheap. Moves tend to be slow and small — setups may feel sluggish.', nudge: 'Size normally, but don\'t expect big swings.' };
  if (price < 16) return { label: 'Calm',         color: 'var(--up)',   note: 'Low fear. Normal trading day expected. Good for disciplined setups.', nudge: 'Normal conditions — execute your plan.' };
  if (price < 20) return { label: 'Mildly nervous', color: 'var(--warn)', note: 'Some caution in the market. Moves a bit bigger than usual. This is fairly normal.', nudge: 'Slight caution, but generally tradeable.' };
  if (price < 25) return { label: 'Nervous',      color: 'var(--warn)', note: 'The market is worried about something. Expect larger swings and faster reversals. Wider stop losses needed.', nudge: 'Reduce size slightly. Wider stops.' };
  if (price < 30) return { label: 'Fearful',      color: 'var(--down)', note: 'High fear. Big moves, sharp reversals, unpredictable behaviour. Options premiums are expensive.', nudge: 'Cut size significantly. Expect whipsaws.' };
  return           { label: 'Panicking',    color: 'var(--down)', note: 'Market is in panic mode. Price can move 3–5% in minutes. Most setups break down completely in these conditions.', nudge: 'Strongly consider sitting out.' };
}

// Nudge priority: returns the single strongest recommendation across all items
function deriveNudge(pm, vixInfo, coilingSymbols) {
  if (!pm?.ready) return null;
  const signals = [];

  // Coil is the strongest disqualifier
  if (pm.coil?.coiling || coilingSymbols.length > 0)
    signals.push({ weight: 10, plan: 'SIT_OUT', reason: 'multi-day coil active — scalping the pin has no edge' });

  // Panic VIX
  if (vixInfo && vixInfo.label === 'Panicking')
    signals.push({ weight: 9, plan: 'SIT_OUT', reason: 'VIX in panic zone — most setups fail in this environment' });

  // Big gap → trend day
  if (pm.gapPct != null && Math.abs(pm.gapPct) >= 1)
    signals.push({ weight: 8, plan: 'TRADE', reason: `large ${pm.gapPct > 0 ? 'gap up' : 'gap down'} — expansion/trend day likely` });

  // Strong daily trend
  if (pm.dailyADX != null && pm.dailyADX >= 25)
    signals.push({ weight: 7, plan: 'TRADE', reason: `daily ADX ${pm.dailyADX.toFixed(0)} — trending, with-trend trades have edge` });

  // Price very close to prior H/L — caution
  const nearLevel = (pm.distToHighPct != null && pm.distToHighPct < 0.4) ||
                    (pm.distToLowPct  != null && pm.distToLowPct  < 0.4);
  if (nearLevel)
    signals.push({ weight: 5, plan: 'WAIT_CONFIRM', reason: 'price within 0.4% of a key level — wait for the reaction before entering' });

  // Choppy daily
  if (pm.dailyADX != null && pm.dailyADX < 18)
    signals.push({ weight: 4, plan: 'WAIT_CONFIRM', reason: `daily ADX ${pm.dailyADX.toFixed(0)} — ranging market, wait for first-30-min confirmation` });

  // Elevated VIX (not panic, but meaningful)
  if (vixInfo && (vixInfo.label === 'Fearful' || vixInfo.label === 'Nervous'))
    signals.push({ weight: 3, plan: 'WAIT_CONFIRM', reason: 'elevated VIX — widen stops and wait for clear direction before sizing' });

  if (signals.length === 0) return null;
  signals.sort((a, b) => b.weight - a.weight);
  return signals[0];
}

// ── Note renderer — shared between edit preview and reminder popup ────────────
// Parses markdown-lite syntax per line:
//   [x] item  → checked checkbox (struck through)
//   [ ] item  → open checkbox
//   - item    → bullet point
//   plain     → plain text
export function renderNote(text, opts = {}) {
  if (!text?.trim()) return null;
  const { fontSize = 13, gap = 6 } = opts;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {text.split('\n').map((line, i) => {
        const chkDone = line.match(/^\[x\]\s*(.*)/i);
        const chkOpen = line.match(/^\[ \]\s*(.*)/);
        const bullet  = line.match(/^[-•]\s*(.*)/);
        if (chkDone) return (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 7, fontSize }}>
            <span style={{ color: 'var(--up)', fontSize: fontSize + 1, lineHeight: 1, flexShrink: 0 }}>☑</span>
            <span style={{ textDecoration: 'line-through', color: 'var(--faint)' }}>{chkDone[1]}</span>
          </div>
        );
        if (chkOpen) return (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 7, fontSize }}>
            <span style={{ color: 'var(--muted)', fontSize: fontSize + 1, lineHeight: 1, flexShrink: 0 }}>☐</span>
            <span style={{ color: 'var(--text)' }}>{chkOpen[1]}</span>
          </div>
        );
        if (bullet) return (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 7, fontSize }}>
            <span style={{ color: 'var(--muted)', fontSize: fontSize - 2, lineHeight: 1.8, flexShrink: 0 }}>●</span>
            <span style={{ color: 'var(--text)' }}>{bullet[1]}</span>
          </div>
        );
        return line.trim()
          ? <div key={i} style={{ fontSize, color: 'var(--muted)' }}>{line}</div>
          : <div key={i} style={{ height: 4 }} />;
      })}
    </div>
  );
}

// ── NoteEditor — textarea with toolbar for inserting markup ──────────────────
function NoteEditor({ value, onChange }) {
  const ref = useRef(null);

  const insert = (prefix) => {
    const el  = ref.current;
    if (!el) return;
    const s   = el.selectionStart;
    const e   = el.selectionEnd;
    const pre = value.slice(0, s);
    const sel = value.slice(s, e);
    const suf = value.slice(e);

    // If cursor is at start of line or beginning, insert prefix on the current line
    // If there's selected text, wrap each selected line
    let newText, newCursor;
    if (sel.includes('\n')) {
      // Multi-line selection — prefix each line
      const lines   = sel.split('\n').map(l => `${prefix}${l}`);
      newText   = pre + lines.join('\n') + suf;
      newCursor = s + lines.join('\n').length;
    } else {
      // No selection or single line — insert at start of current line
      const lineStart = pre.lastIndexOf('\n') + 1;
      const lineText  = value.slice(lineStart, e || value.length);
      // If line already has this prefix, remove it (toggle)
      if (lineText.startsWith(prefix)) {
        newText   = value.slice(0, lineStart) + value.slice(lineStart + prefix.length);
        newCursor = Math.max(lineStart, s - prefix.length);
      } else {
        newText   = value.slice(0, lineStart) + prefix + value.slice(lineStart);
        newCursor = s + prefix.length;
      }
    }

    onChange(newText);
    // Restore cursor after React re-render
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newCursor, newCursor);
    });
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
        {[
          { label: '☐ Checkbox', prefix: '[ ] ', title: 'Add checkbox (type [x] to check it)' },
          { label: '● Bullet',   prefix: '- ',   title: 'Add bullet point' },
        ].map(({ label, prefix, title }) => (
          <button key={prefix} title={title} onMouseDown={e => { e.preventDefault(); insert(prefix); }}
            style={{ fontSize: 10, padding: '2px 9px', borderRadius: 4, border: '1px solid var(--border)',
              background: 'var(--bg)', color: 'var(--muted)', cursor: 'pointer',
              fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '.02em' }}>
            {label}
          </button>
        ))}
        <span style={{ fontSize: 9, color: 'var(--faint)', alignSelf: 'center', marginLeft: 4 }}>
          tip: type [x] to tick a checkbox
        </span>
      </div>

      {/* Textarea */}
      <textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={'[ ] with-trend NTM puts only\n[ ] hard stop -$350\n- no chasing after 10:30'}
        rows={4}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '8px 10px',
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5,
          color: 'var(--text)', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
          resize: 'vertical', lineHeight: 1.65,
        }}
      />
    </div>
  );
}

const PLAN_OPTIONS = [
  { key: 'TRADE',        label: 'Trade it',         color: 'var(--up)',   note: 'You see a clear edge — trade with-trend, size on confirmation.' },
  { key: 'WAIT_CONFIRM', label: 'Wait for confirm',  color: 'var(--warn)', note: 'No clear edge yet — let the first 30 min classify before sizing.' },
  { key: 'SIT_OUT',      label: 'Sit out',           color: 'var(--down)', note: 'Chop, coil, or panic — non-participation is the surer win today.' },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function FirstDecisionGate({ engineOn, liveState, marketOpen, symbolStates, watchedSymbols, vixQuote, onPlanChange }) {
  const today = etDateStr();

  const [plan,          setPlan]          = useState('');
  const [checklist,     setChecklist]     = useState({});
  const [note,          setNote]          = useState('');
  const [committedDate, setCommittedDate] = useState(null);
  const [planLoaded,    setPlanLoaded]    = useState(false);
  const [collapsed,     setCollapsed]     = useState(false);

  const [localPreMarket, setLocalPreMarket] = useState(null);
  const [bootstrapState, setBootstrapState] = useState('idle');
  const fetchedForRef = useRef(null);

  // Load committed plan from KV
  useEffect(() => {
    let cancelled = false;
    dayPlanGet().then(d => {
      if (cancelled) return;
      if (d && d.date === today) {
        setPlan(d.plan || '');
        setChecklist(d.checklist || {});
        setNote(d.note || '');
        setCommittedDate(d.date);
        setCollapsed(true);
        onPlanChange?.(d.plan || '');
      }
      setPlanLoaded(true);
    }).catch(() => setPlanLoaded(true));
    return () => { cancelled = true; };
  }, [today]);

  // Standalone bootstrap — runs on mount, no engine required
  const primarySym = (watchedSymbols ?? ['SPY'])[0];
  useEffect(() => {
    if (fetchedForRef.current === primarySym) return;
    fetchedForRef.current = primarySym;
    setBootstrapState('loading');
    const h = { 'x-session-token': getToken() };
    Promise.all([
      fetch(`${BASE}/chart?symbol=${encodeURIComponent(primarySym)}&interval=1d&range=2mo`, { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${BASE}/quote?symbols=${encodeURIComponent(primarySym)}`, { headers: h }).then(r => r.ok ? r.json() : null),
    ]).then(([chartData, quoteData]) => {
      const bars = parseBars(chartData);
      const quote = quoteData?.[primarySym] ?? quoteData?.quoteResponse?.result?.[0] ?? null;
      const preMarketPrice = quote?.preMarketPrice ?? quote?.regularMarketPrice ?? null;
      setLocalPreMarket(analyzePreMarket(bars, preMarketPrice));
      setBootstrapState('done');
    }).catch(() => setBootstrapState('error'));
  }, [primarySym]); // eslint-disable-line react-hooks/exhaustive-deps

  const minutesIn          = liveState?.minutesIntoSession ?? 0;
  const phase              = !marketOpen ? 'premarket' : minutesIn < 30 ? 'opening' : 'live';
  const effectivePreMarket = liveState?.preMarket ?? localPreMarket;
  const coilingSymbols     = (watchedSymbols ?? []).filter(s => symbolStates?.[s]?.coil?.coiling);
  const vixPrice           = vixQuote?.regularMarketPrice ?? vixQuote?.preMarketPrice ?? null;
  const vixInfo            = classifyVix(vixPrice);
  const nudge              = deriveNudge(effectivePreMarket, vixInfo, coilingSymbols);

  const commit = (chosenPlan) => {
    const rec = { date: today, plan: chosenPlan, checklist, note };
    setPlan(chosenPlan); setCommittedDate(today); setCollapsed(true);
    onPlanChange?.(chosenPlan);
    dayPlanPut(rec).catch(() => {});
  };
  const clearPlan = () => {
    setPlan(''); setCommittedDate(null); setCollapsed(false);
    onPlanChange?.('');
    dayPlanPut({ date: today, plan: '', checklist, note }).catch(() => {});
  };
  const toggleCheck = (key) => setChecklist(c => ({ ...c, [key]: !c[key] }));

  if (!planLoaded) return null;

  const committed = committedDate === today && plan;
  const chosenOpt = PLAN_OPTIONS.find(o => o.key === plan);

  // ── Collapsed bar ─────────────────────────────────────────────────────────
  if (committed && collapsed) {
    return (
      <div style={{ ...gateCard, borderColor: chosenOpt?.color ?? 'var(--border)', background: (chosenOpt?.color ?? 'var(--border)') + '0c' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--faint)' }}>Today's plan</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: chosenOpt?.color }}>{chosenOpt?.label}</span>
            {note && (
              <div style={{ fontSize: 10, color: 'var(--muted)', maxWidth: 280, overflow: 'hidden' }}>
                {renderNote(note, { fontSize: 10, gap: 2 })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LiveVsPlan phase={phase} plan={plan} liveState={liveState} />
            <button onClick={() => setCollapsed(false)} style={smallBtn}>✎ edit</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Expanded gate ──────────────────────────────────────────────────────────
  return (
    <div style={gateCard}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', color: 'var(--text)' }}>
          FIRST DECISION OF THE DAY
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {committed && <button onClick={clearPlan} style={smallBtn}>✕ clear</button>}
          <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 3, background: 'var(--border)', color: 'var(--muted)', letterSpacing: '.05em' }}>
            {phase === 'premarket' ? 'PRE-MARKET' : phase === 'opening' ? `OPENING · ${minutesIn}min` : `LIVE · ${minutesIn}min`}
          </span>
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
        {phase === 'premarket' && 'Market is closed. Review each indicator below, then commit to a plan before the bell.'}
        {phase === 'opening'   && 'First 30 minutes — structure still forming. Hold to your pre-market plan. Don\'t force trades into an unconfirmed open.'}
        {phase === 'live'      && 'Regime is now readable. Review your briefing and check whether the live read matches your plan.'}
      </div>

      {/* ── Pre-flight checklist ────────────────────────────────────────── */}
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--faint)', marginBottom: 10 }}>
        Pre-flight briefing
      </div>

      <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>

        {/* 1. Overnight gap */}
        <CheckItem
          itemKey="gap"
          checklist={checklist}
          onToggle={toggleCheck}
          loading={bootstrapState === 'loading'}
          title="Did price jump overnight?"
          reading={effectivePreMarket?.ready ? (
            effectivePreMarket.gapPct != null ? (
              <ReadingBadge
                value={`${effectivePreMarket.gapPct > 0 ? '+' : ''}${effectivePreMarket.gapPct}%`}
                color={Math.abs(effectivePreMarket.gapPct) >= 1 ? (effectivePreMarket.gapPct > 0 ? 'var(--up)' : 'var(--down)') : 'var(--faint)'}
                label={Math.abs(effectivePreMarket.gapPct) >= 1 ? (effectivePreMarket.gapPct > 0 ? 'Gap up' : 'Gap down') : 'Flat open'}
              />
            ) : <ReadingBadge value="No data" color="var(--faint)" />
          ) : null}
          explain={
            effectivePreMarket?.ready && effectivePreMarket.gapPct != null
              ? Math.abs(effectivePreMarket.gapPct) >= 1
                ? `Price opened ${Math.abs(effectivePreMarket.gapPct)}% ${effectivePreMarket.gapPct > 0 ? 'higher' : 'lower'} than yesterday's close overnight. Big gaps like this often signal a directional day — institutions repositioned while you slept. Trade with the gap, don't fight it.`
                : Math.abs(effectivePreMarket.gapPct) >= 0.4
                  ? `Small ${effectivePreMarket.gapPct > 0 ? 'upward' : 'downward'} lean overnight. Not a strong signal on its own — watch the first 15 minutes to see if it holds.`
                  : 'No meaningful overnight move. Price is opening close to where it closed. No directional edge from the gap alone.'
              : 'Gap data unavailable — check pre-market price once it populates.'
          }
          nudge={
            effectivePreMarket?.ready && effectivePreMarket.gapPct != null
              ? Math.abs(effectivePreMarket.gapPct) >= 1
                ? { text: `Lean towards "Trade it" — trade with the gap direction.`, color: 'var(--up)' }
                : { text: 'Gap is small — not a strong signal either way.', color: 'var(--faint)' }
              : null
          }
        />

        {/* 2. Coil / tight range */}
        <CheckItem
          itemKey="coil"
          checklist={checklist}
          onToggle={toggleCheck}
          loading={bootstrapState === 'loading'}
          title="Is price stuck in a tight range?"
          reading={effectivePreMarket?.ready ? (
            effectivePreMarket.coil?.coiling
              ? <ReadingBadge value={`${effectivePreMarket.coil.daysInBand}-day coil`} color="var(--down)" label="Yes — coiling" />
              : <ReadingBadge value="No coil" color="var(--up)" label="Normal range" />
          ) : null}
          explain={
            effectivePreMarket?.ready
              ? effectivePreMarket.coil?.coiling
                ? `${primarySym} has been pinned in a narrow $${effectivePreMarket.coil.bandLow}–$${effectivePreMarket.coil.bandHigh} band for ${effectivePreMarket.coil.daysInBand} days. Trying to scalp inside this range is a losing game — the edge is in the BREAK. Wait for a clean move above $${effectivePreMarket.coil.bandHigh} or below $${effectivePreMarket.coil.bandLow}, then trade that direction aggressively.`
                : 'Price is moving freely — not coiling. No reason to sit out on this basis.'
              : 'Coil data unavailable — will populate once engine has daily bars.'
          }
          nudge={
            effectivePreMarket?.ready
              ? effectivePreMarket.coil?.coiling
                ? { text: 'Lean towards "Sit out" or "Wait for confirm" — don\'t scalp the pin.', color: 'var(--down)' }
                : { text: 'No coil — not a concern today.', color: 'var(--faint)' }
              : null
          }
        />

        {/* 3. Is the market trending or choppy? */}
        <CheckItem
          itemKey="trend"
          checklist={checklist}
          onToggle={toggleCheck}
          loading={bootstrapState === 'loading'}
          title="Is the market trending or going sideways?"
          reading={effectivePreMarket?.ready && effectivePreMarket.dailyADX != null ? (
            <ReadingBadge
              value={`ADX ${effectivePreMarket.dailyADX}`}
              color={effectivePreMarket.dailyADX >= 25 ? 'var(--up)' : effectivePreMarket.dailyADX < 18 ? 'var(--down)' : 'var(--warn)'}
              label={effectivePreMarket.dailyADX >= 25 ? `Trending ${effectivePreMarket.dailyTrend ?? ''}` : effectivePreMarket.dailyADX < 18 ? 'Choppy' : 'Mixed'}
            />
          ) : null}
          explain={
            effectivePreMarket?.ready && effectivePreMarket.dailyADX != null
              ? effectivePreMarket.dailyADX >= 25
                ? `ADX ${effectivePreMarket.dailyADX} — the market has been making consistent directional moves (${effectivePreMarket.dailyTrend ?? 'unknown direction'}). This is good for directional trades. Trade with the trend, don't try to pick tops or bottoms.`
                : effectivePreMarket.dailyADX < 18
                  ? `ADX ${effectivePreMarket.dailyADX} — the market has been going sideways and bouncing around. Directional trades are hard here because price keeps reversing. Mean-reversion (fading extremes) works better in this environment.`
                  : `ADX ${effectivePreMarket.dailyADX} — somewhere between trending and choppy. No strong edge either way — let the first 30 minutes tell you which type of day it is.`
              : 'ADX unavailable — engine needs to run to compute this from daily bars.'
          }
          nudge={
            effectivePreMarket?.ready && effectivePreMarket.dailyADX != null
              ? effectivePreMarket.dailyADX >= 25
                ? { text: 'Lean towards "Trade it" — trending conditions favour directional trades.', color: 'var(--up)' }
                : effectivePreMarket.dailyADX < 18
                  ? { text: 'Lean towards "Wait for confirm" or "Sit out" — choppy days bleed scalpers.', color: 'var(--down)' }
                  : { text: 'Mixed — wait for the opening 30 min to classify.', color: 'var(--warn)' }
              : null
          }
        />

        {/* 4. VIX / market fear */}
        <CheckItem
          itemKey="vix"
          checklist={checklist}
          onToggle={toggleCheck}
          loading={vixPrice == null && bootstrapState === 'loading'}
          title="How scared is the market right now?"
          reading={vixInfo ? (
            <ReadingBadge
              value={`VIX ${vixPrice?.toFixed(1)}`}
              color={vixInfo.color}
              label={vixInfo.label}
            />
          ) : vixPrice == null ? null : <ReadingBadge value="—" color="var(--faint)" />}
          explain={
            vixInfo
              ? `VIX ${vixPrice?.toFixed(1)} (${vixInfo.label}). ${vixInfo.note} VIX under 15 = calm market. 15–20 = cautious. 20–25 = nervous. Above 25 = fear. Above 30 = panic.`
              : 'VIX unavailable — start the engine or check connectivity.'
          }
          nudge={vixInfo ? { text: vixInfo.nudge, color: vixInfo.color } : null}
        />

        {/* 5. Big news today */}
        <CheckItem
          itemKey="news"
          checklist={checklist}
          onToggle={toggleCheck}
          title="Any big news today?"
          manual
          explain="Check: earnings reports, Fed rate decisions, CPI/jobs data, geopolitical events. These can make any technical setup meaningless — price can move 3–5% in seconds after a surprise announcement. You need to know about scheduled events before you trade."
          nudge={{ text: 'If yes → consider sitting out until after the announcement, or size down significantly.', color: 'var(--warn)' }}
        />

        {/* 6. Price near a key level? */}
        <CheckItem
          itemKey="level"
          checklist={checklist}
          onToggle={toggleCheck}
          loading={bootstrapState === 'loading'}
          title="Is price near an important price level?"
          reading={effectivePreMarket?.ready && (effectivePreMarket.priorHigh != null || effectivePreMarket.priorLow != null) ? (() => {
            const nearHigh = effectivePreMarket.distToHighPct != null && effectivePreMarket.distToHighPct < 0.5;
            const nearLow  = effectivePreMarket.distToLowPct  != null && effectivePreMarket.distToLowPct  < 0.5;
            if (nearHigh) return <ReadingBadge value={`+${effectivePreMarket.distToHighPct}% to high`} color="var(--warn)" label="Near prior high" />;
            if (nearLow)  return <ReadingBadge value={`-${effectivePreMarket.distToLowPct}% to low`}  color="var(--warn)" label="Near prior low"  />;
            return <ReadingBadge
              value={`H $${effectivePreMarket.priorHigh} / L $${effectivePreMarket.priorLow}`}
              color="var(--faint)"
              label="Not near a level"
            />;
          })() : null}
          explain={
            effectivePreMarket?.ready
              ? (() => {
                  const nearHigh = effectivePreMarket.distToHighPct != null && effectivePreMarket.distToHighPct < 0.5;
                  const nearLow  = effectivePreMarket.distToLowPct  != null && effectivePreMarket.distToLowPct  < 0.5;
                  if (nearHigh) return `Price is only ${effectivePreMarket.distToHighPct}% away from yesterday's high ($${effectivePreMarket.priorHigh}). This is a decision zone — price either breaks through (bullish, potential trend continuation) or gets rejected (bearish, possible reversal). Don't enter blindly — wait and watch the first test.`;
                  if (nearLow)  return `Price is only ${effectivePreMarket.distToLowPct}% away from yesterday's low ($${effectivePreMarket.priorLow}). Key support level — watch for a bounce or a break below. Don't enter until you see how price reacts.`;
                  return `Yesterday's high was $${effectivePreMarket.priorHigh} (+${effectivePreMarket.distToHighPct}% away) and low was $${effectivePreMarket.priorLow} (-${effectivePreMarket.distToLowPct}% away). Price has room to move — no immediate key level overhead or below.`;
                })()
              : 'Level data unavailable — will populate once prior-day bars load.'
          }
          nudge={
            effectivePreMarket?.ready &&
            ((effectivePreMarket.distToHighPct != null && effectivePreMarket.distToHighPct < 0.5) ||
             (effectivePreMarket.distToLowPct  != null && effectivePreMarket.distToLowPct  < 0.5))
              ? { text: 'Wait for the first test of the level before entering — don\'t anticipate it.', color: 'var(--warn)' }
              : { text: 'No key level immediately in play — not a concern.', color: 'var(--faint)' }
          }
        />

      </div>

      {/* ── Composite nudge ────────────────────────────────────────────────── */}
      {nudge && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 6,
          border: `1px solid ${nudge.plan === 'TRADE' ? 'var(--up)' : nudge.plan === 'SIT_OUT' ? 'var(--down)' : 'var(--warn)'}`,
          background: (nudge.plan === 'TRADE' ? 'var(--up)' : nudge.plan === 'SIT_OUT' ? 'var(--down)' : 'var(--warn)') + '10',
          display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span style={{ fontSize: 14, lineHeight: 1 }}>
            {nudge.plan === 'TRADE' ? '↑' : nudge.plan === 'SIT_OUT' ? '⊟' : '⏳'}
          </span>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 2 }}>
              SUGGESTED: {PLAN_OPTIONS.find(o => o.key === nudge.plan)?.label?.toUpperCase()}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text)' }}>
              Indicators point to <strong>{PLAN_OPTIONS.find(o => o.key === nudge.plan)?.label}</strong> — {nudge.reason}. You make the final call.
            </div>
          </div>
        </div>
      )}

      {/* Optional plan note with checkbox/bullet toolbar */}
      <NoteEditor value={note} onChange={setNote} />

      {/* Commit buttons */}
      <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--faint)', marginBottom: 6 }}>
        Commit to a plan for today
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        {PLAN_OPTIONS.map(opt => (
          <button key={opt.key} onClick={() => commit(opt.key)} title={opt.note}
            style={{
              padding: '10px 8px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
              border: `1px solid ${plan === opt.key ? opt.color : 'var(--border)'}`,
              background: plan === opt.key ? opt.color : 'transparent',
              color: plan === opt.key ? 'var(--bg)' : opt.color,
              fontWeight: 700, fontSize: 12, transition: 'all .15s',
            }}>
            {opt.label}
          </button>
        ))}
      </div>
      {PLAN_OPTIONS.find(o => o.key === plan) && (
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--muted)', textAlign: 'center' }}>
          {PLAN_OPTIONS.find(o => o.key === plan).note}
        </div>
      )}
    </div>
  );
}

// ── CheckItem — a single briefing row ─────────────────────────────────────────
// reading = rendered badge (auto-answered), manual = checkbox only (no auto data)
function CheckItem({ itemKey, checklist, onToggle, loading, title, reading, explain, nudge, manual }) {
  const [open, setOpen] = useState(false);
  const checked = !!checklist[itemKey];

  return (
    <div style={{
      border: `1px solid ${checked ? 'var(--border-strong)' : 'var(--border)'}`,
      borderRadius: 6, overflow: 'hidden',
      background: checked ? 'var(--bg-hover)' : 'var(--bg)',
      opacity: loading && !manual ? 0.6 : 1,
      transition: 'all .15s',
    }}>
      {/* Row header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        {/* Checkbox */}
        <input type="checkbox" checked={checked}
          onChange={e => { e.stopPropagation(); onToggle(itemKey); }}
          onClick={e => e.stopPropagation()}
          style={{ accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0, width: 14, height: 14 }}
        />
        {/* Title */}
        <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: checked ? 600 : 400, flex: 1 }}>
          {title}
        </span>
        {/* Auto reading badge */}
        {loading && !manual && (
          <span style={{ fontSize: 9, color: 'var(--faint)', fontFamily: 'monospace' }}>loading…</span>
        )}
        {!loading && reading}
        {/* Expand arrow */}
        <span style={{ fontSize: 9, color: 'var(--faint)', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Expanded explanation */}
      {open && (
        <div style={{ padding: '0 10px 10px 34px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.6, marginTop: 8 }}>
            {explain}
          </div>
          {nudge && (
            <div style={{ marginTop: 6, fontSize: 10, fontStyle: 'italic', color: nudge.color }}>
              → {nudge.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── ReadingBadge ──────────────────────────────────────────────────────────────
function ReadingBadge({ value, color, label }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
        {value}
      </span>
      {label && (
        <span style={{ fontSize: 8, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      )}
    </div>
  );
}

// ── Live vs Plan ─────────────────────────────────────────────────────────────
function LiveVsPlan({ phase, plan, liveState }) {
  if (phase !== 'live' || !liveState?.bars || liveState.bars.length < 10) {
    return <span style={{ fontSize: 10, color: 'var(--faint)' }}>
      {phase === 'opening' ? '⏳ Holding plan · classifying…' : ''}
    </span>;
  }
  const adx = liveState.adx;
  const liveTrend = adx == null ? null : adx >= 25 ? 'trend' : adx < 18 ? 'range' : 'mixed';
  if (!liveTrend) return null;

  let msg, color;
  if (plan === 'SIT_OUT' && liveTrend === 'trend') {
    msg = '⚠ Live shows TREND — you planned to sit out.'; color = 'var(--warn)';
  } else if (plan === 'TRADE' && liveTrend === 'range') {
    msg = '⚠ Live shows RANGE/CHOP — cut size or stand down.'; color = 'var(--down)';
  } else if (plan === 'WAIT_CONFIRM') {
    msg   = liveTrend === 'trend' ? '✓ Confirmed TREND — clear to participate.' : liveTrend === 'range' ? '✗ Confirmed RANGE — stay out.' : 'Still mixed — keep waiting.';
    color = liveTrend === 'trend' ? 'var(--up)' : liveTrend === 'range' ? 'var(--down)' : 'var(--warn)';
  } else {
    msg = `✓ Live (${liveTrend}) aligns with plan.`; color = 'var(--up)';
  }
  return <span style={{ fontSize: 10, color, fontWeight: 600, maxWidth: 280, textAlign: 'right', lineHeight: 1.3 }}>{msg}</span>;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const gateCard = {
  background: 'var(--bg-elev, var(--bg))',
  border: '1px solid var(--border)',
  borderRadius: 8, padding: 14, marginBottom: 16,
};
const smallBtn = {
  fontSize: 9, padding: '2px 7px', border: '1px solid var(--border)',
  borderRadius: 3, background: 'transparent', color: 'var(--faint)', cursor: 'pointer',
};
